/**
 * Parsed models, kept for as long as one person stays signed in.
 *
 * Reopening a finished model was slow in a way the network had already been
 * fixed for. The artifact route sends `private, no-cache` with the artifact's
 * own sha256 as its validator, so a second visit is a 304 with no body - the
 * bytes cost nothing. What was not free was everything after them: the GLB was
 * parsed from scratch on every mount, and parsing a 500k-face model is the
 * dominant CPU cost of opening the screen. The browser cannot cache that; only
 * we can.
 *
 * So this holds the *parsed* result. A reopen inside one signed-in session
 * skips the parse entirely.
 *
 * ## What the key has to contain, and why each part is load-bearing
 *
 * **The viewer's identity.** `userId` is in the key and the cache is emptied
 * on sign-out, both. Either alone would be a hole: without the key, a second
 * account signing in on the same browser profile before the clear ran could
 * read the first one's model; without the clear, the geometry of somebody
 * else's capture would sit in memory after they left. This is the same risk
 * the artifact route's `no-cache` was chosen to answer, one layer up, and it
 * has to be answered again here because a JavaScript Map has no `Vary`.
 *
 * **The artifact's digest.** Its `byteSize` and id together identify the
 * bytes; a re-run writes a *new* artifact with a new id, so an id that comes
 * back always means the same file. Keying on the job alone would serve v1's
 * geometry for v2.
 *
 * ## Why it is this small
 *
 * Two entries. A model is tens of megabytes of typed arrays, and the case
 * worth serving is "the one I was just looking at, and maybe the one before" -
 * moving between the model tab and the outputs tab, or between two versions of
 * the same capture. A larger cache would trade a page's worth of memory for
 * hits nobody has.
 *
 * Nothing here is persisted. `localStorage` cannot hold typed arrays of this
 * size and a model that survived a browser restart would be exactly the leak
 * the sign-out clear exists to prevent.
 */

import type { Mesh } from "./glb";

/** How many parsed models are kept. See the note above on why it is two. */
export const MESH_CACHE_LIMIT = 2;

interface Entry {
  key: string;
  mesh: Mesh;
}

/**
 * Insertion-ordered, newest last. An array rather than a Map because the
 * eviction rule is "drop the oldest" and two elements make the cost of
 * scanning irrelevant.
 */
let entries: Entry[] = [];

function keyOf(
  userId: string,
  jobId: string,
  artifactId: string,
  byteSize: number | null,
): string {
  // The separator cannot appear in a UUID, so no two different triples can
  // collide by concatenation.
  return `${userId}|${jobId}|${artifactId}|${byteSize ?? "?"}`;
}

/**
 * The parsed model for this exact artifact, seen by this exact person.
 *
 * Returns `null` for a miss, never a stale or approximate match.
 */
export function cachedMesh(
  userId: string | null,
  jobId: string | null,
  artifactId: string | null,
  byteSize: number | null,
): Mesh | null {
  // No identity, no cache. An anonymous read cannot be attributed to anyone,
  // so it must not be stored under anyone either.
  if (!userId || !jobId || !artifactId) return null;
  const key = keyOf(userId, jobId, artifactId, byteSize);
  const found = entries.find((entry) => entry.key === key);
  if (!found) return null;
  // Touch: a hit makes it the newest, so the model somebody keeps returning to
  // is not the one evicted by a single visit to another.
  entries = [...entries.filter((entry) => entry.key !== key), found];
  return found.mesh;
}

/** Remember a parsed model. Silently does nothing without an identity. */
export function rememberMesh(
  userId: string | null,
  jobId: string | null,
  artifactId: string | null,
  byteSize: number | null,
  mesh: Mesh,
): void {
  if (!userId || !jobId || !artifactId) return;
  const key = keyOf(userId, jobId, artifactId, byteSize);
  entries = [...entries.filter((entry) => entry.key !== key), { key, mesh }];
  if (entries.length > MESH_CACHE_LIMIT) {
    entries = entries.slice(entries.length - MESH_CACHE_LIMIT);
  }
}

/**
 * Drop everything. Called on sign-out.
 *
 * Belt and braces with the `userId` in the key: the key alone would already
 * stop User B reading User A's model, and this stops A's geometry sitting in a
 * shared browser's memory after A has left. Neither is sufficient on its own,
 * and neither is expensive.
 */
export function forgetAllMeshes(): void {
  entries = [];
}

/** How many entries are held. For tests; nothing in the product reads it. */
export function cachedMeshCount(): number {
  return entries.length;
}
